import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, fmtMoney } from "@/lib/api";
import { PageHeader, Panel, LoadingState, ErrorState } from "@/components/common";
import {
  LayoutDashboard, Wallet, TrendingUp, TrendingDown, Layers, Activity,
  ClipboardList, Cpu, ShieldAlert, Plug, Globe, Zap,
} from "lucide-react";
import {
  AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { cn } from "@/lib/utils";

function Kpi({ label, value, sub, icon: Icon, tone = "neutral", testId }) {
  const toneClass = tone === "pos" ? "text-emerald-400" : tone === "neg" ? "text-rose-400" : "text-slate-100";
  return (
    <div data-testid={testId} className="rounded-lg border border-[var(--border-subtle)] bg-[var(--card)] p-4 hover:border-[var(--border-active)] transition-colors">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</span>
        <Icon className="w-4 h-4 text-slate-600" />
      </div>
      <div className={cn("font-mono-num text-lg sm:text-xl font-bold whitespace-nowrap", toneClass)}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function MiniStat({ label, value, icon: Icon, accent }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-[var(--border-subtle)] bg-[var(--card)] px-3 py-2.5">
      <div className={cn("w-8 h-8 rounded-md flex items-center justify-center shrink-0", accent)}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
        <div className="font-mono-num text-sm font-semibold text-slate-100 truncate">{value}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [picked, setCurveCcy] = useState(null);
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["dashboard"],
    refetchInterval: 15000,
    queryFn: async () => (await api.get("/dashboard/summary")).data,
  });

  if (isLoading) return <LoadingState label="Loading command center..." />;
  if (isError) return <ErrorState onRetry={refetch} />;

  const activityTone = { buy: "text-blue-400", win: "text-emerald-400", deposit: "text-emerald-400", info: "text-slate-400" };
  const inr = data.paper?.INR || {};
  const usdt = data.paper?.USDT || {};
  const curveCcy = picked || ((data.paper_curve?.INR?.length || 0) < 2 && (data.paper_curve?.USDT?.length || 0) >= 2 ? "USDT" : "INR");
  const curve = data.paper_curve?.[curveCcy] || [];
  const signed = (v) => (Number(v) >= 0 ? "pos" : "neg");
  const usd = (v) => (v == null ? "—" : `${Number(v).toLocaleString("en-US", { maximumFractionDigits: 2 })} USDT`);

  return (
    <div>
      <PageHeader
        title="Overview Dashboard"
        subtitle="Paper-trading results (simulation) · live trading disabled"
        icon={LayoutDashboard}
        testId="dashboard-header"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
        <Kpi testId="kpi-paper-equity-inr" label="Paper Equity · INR" value={fmtMoney(inr.equity)} sub="Sharekhan book" icon={Wallet} />
        <Kpi testId="kpi-paper-equity-usdt" label="Paper Equity · USDT" value={usd(usdt.equity)} sub="Binance book" icon={Layers} />
        <Kpi testId="kpi-daily" label="Today P&L · INR" value={fmtMoney(inr.daily_pnl)} tone={signed(inr.daily_pnl)} sub={`USDT ${usd(usdt.daily_pnl)}`} icon={Activity} />
        <Kpi testId="kpi-realized" label="Realized P&L · INR" value={fmtMoney(inr.realized_pnl)} tone={signed(inr.realized_pnl)} sub={`USDT ${usd(usdt.realized_pnl)}`} icon={TrendingUp} />
        <Kpi testId="kpi-unrealized" label="Unrealized P&L · INR" value={fmtMoney(inr.unrealized_pnl)} tone={signed(inr.unrealized_pnl)} sub={`USDT ${usd(usdt.unrealized_pnl)}`} icon={Number(inr.unrealized_pnl) >= 0 ? TrendingUp : TrendingDown} />
        <Kpi testId="kpi-contributed" label="Owner Capital (recorded)" value={fmtMoney(data.contributed_capital)} sub="From your deposits ledger" icon={Briefcaselike} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <MiniStat label="Open Positions" value={data.open_positions} icon={Activity} accent="bg-blue-500/10 text-blue-400" />
        <MiniStat label="Pending Orders" value={data.pending_orders} icon={ClipboardList} accent="bg-amber-500/10 text-amber-400" />
        <MiniStat label="Kill Switch" value={data.kill_switch_engaged ? "ENGAGED" : "Off"} icon={Cpu} accent={data.kill_switch_engaged ? "bg-rose-500/10 text-rose-400" : "bg-emerald-500/10 text-emerald-400"} />
        <MiniStat label="Max Exposure Used" value={`${data.risk_utilization_pct}%`} icon={ShieldAlert} accent="bg-rose-500/10 text-rose-400" />
        <MiniStat label="Reconciliation" value={data.reconciliation_status === "reconciled" ? "OK" : `${data.discrepancy_count} flagged`} icon={Plug} accent={data.reconciliation_status === "reconciled" ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"} />
        <MiniStat label="Market" value={data.market_status} icon={Globe} accent="bg-slate-500/10 text-slate-400" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Panel
          title={`Paper Equity Curve · ${curveCcy}`}
          testId="capital-growth-panel"
          className="xl:col-span-2"
          actions={<div className="flex gap-1">{["INR", "USDT"].map((c) => (
            <button key={c} data-testid={`curve-ccy-${c}`} onClick={() => setCurveCcy(c)} className={cn("px-2 py-0.5 rounded text-[11px]", curveCcy === c ? "bg-blue-500/20 text-blue-300" : "text-slate-400")}>{c}</button>))}</div>}
        >
          <div className="p-4 h-[320px]" style={{ minHeight: 280, minWidth: 0 }}>
            {curve.length < 2 ? (
              <div data-testid="equity-curve-empty" className="h-full flex items-center justify-center text-xs text-slate-500">The curve appears after your first paper trades.</div>
            ) : (
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={260}>
              <AreaChart data={curve}>
                <defs>
                  <linearGradient id="eq" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#1E293B" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "#1E293B" }} minTickGap={40} />
                <YAxis tick={{ fill: "#64748B", fontSize: 10 }} tickLine={false} axisLine={false} width={60}
                  tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ background: "#171E2E", border: "1px solid #2D3D59", borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: "#94A3B8" }}
                  formatter={(v) => [Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 }), `Equity ${curveCcy}`]}
                />
                <Area type="monotone" dataKey="equity" stroke="#3B82F6" strokeWidth={2} fill="url(#eq)" />
              </AreaChart>
            </ResponsiveContainer>
            )}
          </div>
        </Panel>

        <Panel title="Recent Paper Activity" testId="recent-activity-panel">
          <div className="divide-y divide-[var(--border-subtle)]">
            {data.recent_activity.length === 0 && <div className="px-4 py-6 text-xs text-slate-500" data-testid="activity-empty">No paper trades yet. Place one on the Paper Trading page.</div>}
            {data.recent_activity.map((a, i) => (
              <div key={i} className="flex items-start gap-3 px-4 py-3">
                <span className={cn("mt-1 w-1.5 h-1.5 rounded-full shrink-0", a.type === "win" || a.type === "deposit" ? "bg-emerald-400" : a.type === "buy" ? "bg-blue-400" : "bg-slate-500")} />
                <span className={cn("text-xs", activityTone[a.type] || "text-slate-400")}>{a.text}</span>
              </div>
            ))}
          </div>
          <div className="px-4 py-3 border-t border-[var(--border-subtle)] flex items-center gap-2">
            <Zap className="w-3.5 h-3.5 text-slate-600" />
            <span className="text-[11px] text-slate-500">Automation: <span className="text-amber-400 font-mono">{data.automation_status}</span></span>
          </div>
        </Panel>
      </div>
    </div>
  );
}

function Briefcaselike(props) {
  return <Layers {...props} />;
}
